import React from 'react';
import {graphql, useFragment} from 'react-relay';
import {useIntl} from 'react-intl';
import Styled from 'styled-components/macro';
import {FlexColumn, Text, Toggle} from '@forecast-it/design-system';
import SettingsProjectSubNav from './SettingsProjectSubNav';
import {createToast} from '../../shared/components/toasts/toast';
import UpdateCompanyMutation from '../../../mutations/update_company_mutation.modern';
import Util from '../../shared/util/util';
import * as tracking from '../../../tracking';
import {ForecastThemeProvider} from '../../shared/ForecastThemeProvider';

// Page chrome only - width and offset. Everything with a colour, a font size or a flex
// direction is a design-system component below, not styling here.
const Page = Styled.div`
	max-width: 620px;
	margin-left: 24px;
	margin-top: 6px;
`;

// Company-wide live rule, not a per-project default: there is no project-level value to
// inherit or bulk-apply, which is what makes this tab different from Task Monitoring beside
// it. Hence a single toggle and an "applies immediately" scope note, rather than Task
// Monitoring's radio group plus "existing projects keep their setting".
export const changeCascadeDependencyDates = (companyId, enabled, formatMessage) => {
	Util.CommitMutation(
		UpdateCompanyMutation,
		{
			id: companyId,
			cascadeDependencyDates: enabled,
		},
		data => {
			if (data) {
				// Tracked inside the success callback, not before the commit, so a rejected
				// mutation is not counted as a change. Failures surface through
				// Util.CommitMutation's standard error modal and Relay rolls the optimistic
				// toggle back, so no extra onError is needed here.
				tracking.trackEvent('Settings - Cascade Dependency Dates - Changed', {value: enabled});
				createToast({
					duration: 5000,
					message: formatMessage({id: 'common.changes_saved'}),
				});
			}
		}
	);
};

export const SettingsTaskDependenciesComponent = ({viewer}) => {
	const {formatMessage} = useIntl();
	const {id: companyId, cascadeDependencyDates} = viewer.company;

	// Toggle hands onChange the value it is moving TO, so this is the value to store.
	const onChange = enabled => changeCascadeDependencyDates(companyId, enabled, formatMessage);

	return (
		<div className="section-content settings-app" data-cy={'settings-task-dependencies-page'}>
			<ForecastThemeProvider>
				<SettingsProjectSubNav />
				<Page>
					<FlexColumn gap={'m'}>
						<Text color={Text.Color.auxiliary}>
							{formatMessage({id: 'settings.task-dependencies-description'})}
						</Text>

						<FlexColumn gap={'s'}>
							<Toggle checked={!!cascadeDependencyDates} onChange={onChange} cy={'cascade-dependency-dates'}>
								<Text>{formatMessage({id: 'settings.cascade-dependency-dates'})}</Text>
							</Toggle>
							<Text color={Text.Color.auxiliary}>
								{formatMessage({id: 'settings.cascade-dependency-dates-description'})}
							</Text>
						</FlexColumn>

						<Text color={Text.Color.auxiliary} data-cy={'task-dependencies-scope-note'}>
							{formatMessage({id: 'settings.task-dependencies-scope-note'})}
						</Text>
						{/* Without this the scope note above overpromises: cascade never runs on a
						    project whose dates Jira or Azure DevOps own. */}
						<Text color={Text.Color.auxiliary} data-cy={'task-dependencies-synced-projects-note'}>
							{formatMessage({id: 'settings.task-dependencies-synced-projects-note'})}
						</Text>
					</FlexColumn>
				</Page>
			</ForecastThemeProvider>
		</div>
	);
};

export const SettingsTaskDependenciesQuery = graphql`
	query SettingsTaskDependenciesQuery {
		viewer {
			actualPersonId
			component(name: "settings_task_dependencies")
			...SettingsTaskDependencies_viewer
		}
	}
`;

const VIEWER_FRAGMENT = graphql`
	fragment SettingsTaskDependencies_viewer on Viewer {
		actualPersonId
		id
		company {
			id
			cascadeDependencyDates
		}
	}
`;

// useFragment, not createFragmentContainer - new components consume Relay data with the
// hook. The query boundary stays on ForecastQueryRenderer in AdminRoutes; useFragment only
// consumes data, it does not load it.
const SettingsTaskDependencies = ({viewer}) => {
	const data = useFragment(VIEWER_FRAGMENT, viewer);
	return <SettingsTaskDependenciesComponent viewer={data} />;
};

export default SettingsTaskDependencies;
